import React, { useEffect, useState } from 'react';
import { Card, Badge, FadeIn, TopBar, Sheet } from '../components/ui';
import { rupee, fmtDate } from '../theme';
import api from '../api/client';

const statusTone = { in_progress: 'brand', completed: 'success', assigned: 'warning', pending: 'warning', cancelled: 'neutral' };

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
];

export default function ServiceHistory({ onClose }) {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [sortNewest, setSortNewest] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/bookings')
      .then((d) => { if (alive && d?.bookings) setBookings(d.bookings); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  let list = bookings.filter((b) => (filter === 'all' ? true : b.status === filter));
  list = [...list].sort((a, b) => {
    const da = new Date(a.createdAt).getTime();
    const db = new Date(b.createdAt).getTime();
    return sortNewest ? db - da : da - db;
  });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <TopBar
        title="Service History" onClose={onClose} closeIcon="←"
        action={
          <button className="topbar__action" onClick={() => setSortNewest((s) => !s)}>
            {sortNewest ? 'Newest ↓' : 'Oldest ↑'}
          </button>
        }
      />

      <div className="chip-wrap" style={{ padding: '16px 16px 6px' }}>
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} className={`chip ${filter === f.key ? 'chip--on' : ''}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div style={{ padding: 16, paddingBottom: 40, display: 'grid', gap: 12 }}>
        {list.length === 0 ? (
          <p className="muted center" style={{ padding: '40px 0' }}>No services in this view.</p>
        ) : list.map((b, i) => (
          <FadeIn key={b._id} delay={i * 45}>
            <Card onClick={() => setDetail(b)}>
              <div className="row-between">
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--primary)' }}>{b.bookingCode}</span>
                <Badge label={(b.status || '').replace('_', ' ')} tone={statusTone[b.status] || 'neutral'} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 10 }}>{b.packageTitle}</div>
              <div className="row-between" style={{ marginTop: 10 }}>
                <span className="muted" style={{ fontSize: 13 }}>🗓️ {fmtDate(b.createdAt)}</span>
                <span style={{ fontSize: 15, fontWeight: 800 }}>{rupee(b.amount)}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700, marginTop: 10 }}>Tap for details ›</div>
            </Card>
          </FadeIn>
        ))}
      </div>

      <Sheet open={!!detail} onClose={() => setDetail(null)}>
        {detail && (
          <>
            <div className="row-between" style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 18, fontWeight: 800 }}>{detail.packageTitle}</span>
              <Badge label={(detail.status || '').replace('_', ' ')} tone={statusTone[detail.status] || 'neutral'} />
            </div>
            <div className="muted" style={{ fontSize: 13 }}>{detail.bookingCode} · {rupee(detail.amount)}</div>

            <div className="row-between" style={{ background: 'var(--bg)', borderRadius: 'var(--r-md)', padding: 14, marginTop: 16 }}>
              <div className="center grow">
                <div className="faint" style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1 }}>FROM</div>
                <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 3 }}>{fmtDate(detail.createdAt)}</div>
              </div>
              <span className="faint">→</span>
              <div className="center grow">
                <div className="faint" style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1 }}>
                  {detail.status === 'completed' ? 'COMPLETED' : 'ONGOING'}
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 3 }}>
                  {detail.status === 'completed' ? fmtDate(detail.updatedAt) : '—'}
                </div>
              </div>
            </div>

            <h3 style={{ fontSize: 15, fontWeight: 800, marginTop: 20, marginBottom: 10 }}>Assigned staff</h3>
            {detail.staff ? (
              <div className="card row" style={{ gap: 14 }}>
                <span style={{
                  width: 64, height: 76, borderRadius: 10, background: 'var(--bg)',
                  border: '1px solid var(--border)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', overflow: 'hidden', fontSize: 26, flex: 'none',
                }}>
                  {detail.staff.profilePhoto
                    ? <img src={detail.staff.profilePhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : '👷'}
                </span>
                <div className="grow">
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{detail.staff.name}</div>
                  {detail.staff.staffId ? (
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--primary)', letterSpacing: 1, marginTop: 2 }}>{detail.staff.staffId}</div>
                  ) : null}
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>⭐ {detail.staff.rating || '—'}</div>
                  {detail.staff.mobile ? (
                    <a href={`tel:+91${String(detail.staff.mobile).slice(-10)}`}
                      style={{ fontSize: 13.5, color: 'var(--success)', fontWeight: 700, marginTop: 4, display: 'block' }}>
                      📞 {detail.staff.mobile}
                    </a>
                  ) : (
                    <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>Number visible only during active service</div>
                  )}
                </div>
              </div>
            ) : (
              <p className="muted">No staff assigned yet.</p>
            )}
          </>
        )}
      </Sheet>
    </div>
  );
}
